import { Flex } from "@mantine/core";
import { useCallback, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import SearchFilters from "src/components/pages/Search/SearchFilters";
import SearchHeader from "src/components/pages/Search/SearchHeader";
import ResultsTable from "src/components/shared/Search/ResultsTable";
import { useGetApi } from "src/hooks";
import { useDispatch } from "src/redux/hooks";
import { resetSearchSlice } from "src/redux/slices/searchSlice";
import { PageContainer } from "src/styled";
import { MEDLBID } from "src/utils";

import {
  type ISearchForm,
  type ISearchFormWithUndefined,
} from "./SearchPage.types";
import {
  getParams,
  getSearchScopeParams,
  mapSearchResults,
  searchInitialValues,
} from "./SearchPage.utils";

const SearchPage = () => {
  const initialRender = useRef(true);
  const dispatch = useDispatch();
  const { collectionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssort = searchParams.get("ssort");
  const sdir = searchParams.get("sdir");
  const type = searchParams.get("type");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
  const { type: _scopeType, ...fromParams } = Object.fromEntries(
    searchParams.entries(),
  ) as any;

  if (fromParams.sub) fromParams.sub = fromParams.sub === "true";
  if (fromParams.startDate)
    fromParams.startDate = new Date(fromParams.startDate);
  if (fromParams.endDate) fromParams.endDate = new Date(fromParams.endDate);
  const defaultValues = useRef({
    ...searchInitialValues,
    ...fromParams,
  }).current;
  const immediateFetch = useRef(
    Boolean(type) || Object.keys(fromParams).length > 0,
  );

  const { register, control, reset } = useForm<ISearchForm>({
    defaultValues,
  });
  const watchData = useWatch({ control });
  const { data, refetch, isLoading } = useGetApi({
    request: "entry-text-search",
    fetchOnInit: false,
    mapper: mapSearchResults,
  });
  const handleSearch = useCallback(
    async ({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
      type: _formType,
      ...data
    }: ISearchFormWithUndefined) => {
      const usedFilters = Object.fromEntries(
        Object.entries(data).filter(
          ([, v]) => v !== null && v !== undefined && v !== "",
        ),
      );
      usedFilters.sub = String(usedFilters.sub);
      if (ssort) usedFilters.ssort = ssort;
      if (sdir) usedFilters.sdir = sdir;
      if (type) usedFilters.type = type;
      setSearchParams(usedFilters as Record<string, string>, { replace: true });
      const params: Record<string, any> = {
        ...getParams(data),
        ...getSearchScopeParams(type, collectionId),
        ssort,
        sdir,
      };
      await refetch({
        params,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionId, refetch, type, ssort, sdir],
  );

  const handleClear = useCallback(() => {
    reset(searchInitialValues);
  }, [reset]);

  useEffect(() => {
    if (immediateFetch.current) {
      initialRender.current = false;
      void handleSearch(watchData);
      immediateFetch.current = false;
      return;
    }

    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (Object.keys(watchData).length === 0) {
      return;
    }

    const timer = setTimeout(async () => {
      await handleSearch(watchData);
    }, 500);

    return () => clearTimeout(timer);
  }, [handleSearch, watchData]);

  useEffect(() => {
    return () => {
      dispatch(resetSearchSlice());
    };
  }, [dispatch]);
  const isMedical = collectionId === MEDLBID;

  return (
    <PageContainer>
      <SearchHeader
        watchData={watchData}
        disabled={!data || data.length === 0}
      />
      <Flex h="calc(100vh - 104px - 1.5% - 120px)" gap={16}>
        <SearchFilters
          register={register}
          control={control}
          onClear={handleClear}
          isMedical={isMedical}
        />
        <ResultsTable
          data={data ?? []}
          loading={isLoading}
          isMedical={isMedical}
        />
      </Flex>
    </PageContainer>
  );
};

export default SearchPage;
